<template>
    <div class="field" :class="$attrs.class" :style="$attrs.style">
        <label v-if="label" :for="id"
            >{{ label }}<sup v-if="required" class="font-semibold text-red-500">*</sup>
            <slot name="disclosure" />
        </label>
        <div class="text-control" :class="{ 'text-area': isTextarea }" :style="!label ? { 'margin-top': '0' } : null">
            <span v-if="prefix" class="prefix">{{ prefix }}</span>
            <component
                ref="formInput"
                :is="isTextarea ? 'textarea' : 'input'"
                :id="id"
                :type="type"
                :value="modelValue"
                @input="updateValue"
                v-bind="attrsWithoutClassAndStyles"
                :required="$attrs.required"
                :class="inputClass"
                :style="inputStyle"
            ></component>
        </div>
        <p v-if="hint && !error" class="ml-1 mt-1 text-sm font-bold text-purple-500 opacity-85">
            {{ hint }}
        </p>
        <FormError :error="error" class="mt-1" />
    </div>
</template>
<script lang="ts">
export default {
    name: 'FormInput',
    inheritAttrs: false,
    customOptions: {},
}
</script>

<script lang="ts" setup>
import { computed, ref, useAttrs } from 'vue'
import FormError, { FormErrorType } from '@/components/FormError.vue'

type InputType = 'text' | 'number' | 'email' | 'password' | 'textarea'
type InputValue = string | number | null | undefined

const props = withDefaults(
    defineProps<{
        label?: string
        id?: string
        type?: InputType
        required?: boolean
        hint?: string
        error?: FormErrorType
        prefix?: string
        inputClass?: string | string[] | Record<string, boolean>
        inputStyle?: string | string[] | Record<string, boolean>
    }>(),
    {
        type: 'text',
        required: false,
    },
)

const modelValue = defineModel<InputValue>()
const formInput = ref<HTMLInputElement | HTMLTextAreaElement>()
const isTextarea = computed(() => props.type === 'textarea')

const attrs = useAttrs()
const attrsWithoutClassAndStyles = computed(() => {
    const { class: _, style: __, ...rest } = attrs
    return rest
})

const updateValue = (event: InputEvent) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement
    let value = target.value

    if (props.type === 'number') {
        const min = Number(attrs.min)

        if (!isNaN(min) && min >= 0 && Number(value) < 0) {
            value = Math.abs(Number(value)).toString()
            target.value = value
        }
    }

    modelValue.value = value
}

defineExpose({
    formInput,
    focus: () => {
        formInput.value?.focus()
    },
})
</script>
